import React, { useState, useEffect } from 'react';
import { tenderAPI } from '../utils/api';
import { Save, Loader2, Plus, X, RefreshCw, Bell, BellOff } from 'lucide-react';

function TenderSettings() {
  var s1 = useState(null); var profile = s1[0]; var setProfile = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(false); var saving = s3[0]; var setSaving = s3[1];
  var s4 = useState(null); var msg = s4[0]; var setMsg = s4[1];
  var s5 = useState(false); var rescoring = s5[0]; var setRescoring = s5[1];
  var s6 = useState(null); var notifConfig = s6[0]; var setNotifConfig = s6[1];

  useEffect(function() { fetchProfile(); fetchNotifConfig(); }, []);

  var fetchNotifConfig = async function() {
    try {
      var res = await tenderAPI.notificationConfig();
      setNotifConfig(res.data);
    } catch (e) { console.error(e); }
  };

  var saveNotifConfig = async function() {
    setSaving(true);
    try {
      await tenderAPI.notificationConfigUpdate(notifConfig);
      setMsg('Notification settings saved');
      setTimeout(function() { setMsg(null); }, 3000);
    } catch (e) { setMsg('Save failed'); }
    finally { setSaving(false); }
  };

  var fetchProfile = async function() {
    try {
      var res = await tenderAPI.getProfile();
      setProfile(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  var handleSave = async function() {
    setSaving(true);
    setMsg(null);
    try {
      await tenderAPI.updateProfile(profile);
      setMsg('Profile saved');
      setTimeout(function() { setMsg(null); }, 3000);
    } catch (e) { setMsg('Save failed'); }
    finally { setSaving(false); }
  };

  var handleRescore = async function() {
    setRescoring(true);
    try {
      var res = await tenderAPI.rescoreAll();
      setMsg(res.data.message || 'Re-scored');
      setTimeout(function() { setMsg(null); }, 3000);
    } catch (e) { setMsg('Re-scoring failed'); }
    finally { setRescoring(false); }
  };

  var updateKeywords = function(tier, lang, value) {
    var np = JSON.parse(JSON.stringify(profile));
    np[tier][lang] = value.split('\n').map(function(l) { return l.trim(); }).filter(Boolean);
    setProfile(np);
  };

  var updateList = function(field, value) {
    var np = JSON.parse(JSON.stringify(profile));
    np[field] = value.split('\n').map(function(l) { return l.trim(); }).filter(Boolean);
    setProfile(np);
  };

  var updateNumber = function(field, value) {
    var np = JSON.parse(JSON.stringify(profile));
    np[field] = parseFloat(value) || 0;
    setProfile(np);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!profile) return <div className="p-6 text-slate-500">Failed to load profile</div>;

  return (
    <div className="p-6 space-y-6" data-testid="tender-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tender Scout Settings</h1>
          <p className="text-sm text-slate-500">Configure matching keywords, target organisations, and scoring thresholds</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleRescore} disabled={rescoring} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="rescore-btn">
            <RefreshCw size={16} className={rescoring ? 'animate-spin' : ''} /> {rescoring ? 'Re-scoring...' : 'Re-score All Tenders'}
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="save-profile-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Settings
          </button>
        </div>
      </div>

      {msg && <div className={'p-3 rounded-lg text-sm ' + (msg.includes('fail') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700')}>{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Tier 1 Keywords <span className="text-xs text-slate-400 font-normal">(+3 score, highest relevance)</span></h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">English</label>
              <textarea value={(profile.tier1_keywords?.en || []).join('\n')} onChange={function(e) { updateKeywords('tier1_keywords', 'en', e.target.value); }} rows={6} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tier1-en-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Bahasa Melayu</label>
              <textarea value={(profile.tier1_keywords?.bm || []).join('\n')} onChange={function(e) { updateKeywords('tier1_keywords', 'bm', e.target.value); }} rows={6} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tier1-bm-input" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Tier 2 Keywords <span className="text-xs text-slate-400 font-normal">(+2 score, related services)</span></h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">English</label>
              <textarea value={(profile.tier2_keywords?.en || []).join('\n')} onChange={function(e) { updateKeywords('tier2_keywords', 'en', e.target.value); }} rows={6} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tier2-en-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Bahasa Melayu</label>
              <textarea value={(profile.tier2_keywords?.bm || []).join('\n')} onChange={function(e) { updateKeywords('tier2_keywords', 'bm', e.target.value); }} rows={6} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="tier2-bm-input" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Tier 3 Keywords <span className="text-xs text-slate-400 font-normal">(+1 score, general consulting)</span></h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">English</label>
              <textarea value={(profile.tier3_keywords?.en || []).join('\n')} onChange={function(e) { updateKeywords('tier3_keywords', 'en', e.target.value); }} rows={5} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Bahasa Melayu</label>
              <textarea value={(profile.tier3_keywords?.bm || []).join('\n')} onChange={function(e) { updateKeywords('tier3_keywords', 'bm', e.target.value); }} rows={5} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Negative Keywords <span className="text-xs text-slate-400 font-normal">(Auto-disqualify, score = 0)</span></h3>
          <textarea value={(profile.negative_keywords || []).join('\n')} onChange={function(e) { updateList('negative_keywords', e.target.value); }} rows={8} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" placeholder="One keyword per line" data-testid="negative-keywords-input" />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Target Organisations <span className="text-xs text-slate-400 font-normal">(+1 score boost)</span></h3>
          <textarea value={(profile.target_organisations || []).join('\n')} onChange={function(e) { updateList('target_organisations', e.target.value); }} rows={8} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" placeholder="One organisation per line" data-testid="target-orgs-input" />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-900">Value Thresholds</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Minimum Value (RM) <span className="text-slate-400">- Below this = -1 score</span></label>
              <input type="number" value={profile.minimum_value || ''} onChange={function(e) { updateNumber('minimum_value', e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="min-value-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">High Value Threshold (RM) <span className="text-slate-400">- Above this = +1 score</span></label>
              <input type="number" value={profile.high_value_threshold || ''} onChange={function(e) { updateNumber('high_value_threshold', e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Flagship Threshold (RM) <span className="text-slate-400">- Flagship opportunities</span></label>
              <input type="number" value={profile.flagship_threshold || ''} onChange={function(e) { updateNumber('flagship_threshold', e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Bell size={16} /> Notification Settings</h3>
            <button onClick={saveNotifConfig} disabled={saving || !notifConfig} className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1" data-testid="save-notif-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
          {notifConfig && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Enable Notifications</p>
                  <p className="text-xs text-slate-400">Receive in-app alerts for tender matches</p>
                </div>
                <button onClick={function() { setNotifConfig({...notifConfig, enabled: !notifConfig.enabled}); }} className={'relative w-11 h-6 rounded-full transition-colors ' + (notifConfig.enabled ? 'bg-primary' : 'bg-slate-300')} data-testid="notif-enabled-toggle">
                  <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' + (notifConfig.enabled ? 'translate-x-5' : '')} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">High-Score Tender Alerts</p>
                  <p className="text-xs text-slate-400">Alert when tenders match above threshold</p>
                </div>
                <button onClick={function() { setNotifConfig({...notifConfig, notify_urgent: !notifConfig.notify_urgent}); }} className={'relative w-11 h-6 rounded-full transition-colors ' + (notifConfig.notify_urgent ? 'bg-primary' : 'bg-slate-300')} data-testid="notif-urgent-toggle">
                  <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' + (notifConfig.notify_urgent ? 'translate-x-5' : '')} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">High-Value Tender Alerts</p>
                  <p className="text-xs text-slate-400">Alert when tender value exceeds threshold</p>
                </div>
                <button onClick={function() { setNotifConfig({...notifConfig, notify_high_value: !notifConfig.notify_high_value}); }} className={'relative w-11 h-6 rounded-full transition-colors ' + (notifConfig.notify_high_value ? 'bg-primary' : 'bg-slate-300')} data-testid="notif-high-value-toggle">
                  <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' + (notifConfig.notify_high_value ? 'translate-x-5' : '')} />
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Minimum Score Threshold</label>
                <p className="text-xs text-slate-400 mb-2">Only alert for tenders scoring at or above this level (1-5)</p>
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map(function(n) {
                    return <button key={n} onClick={function() { setNotifConfig({...notifConfig, min_score: n}); }} className={'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ' + (notifConfig.min_score === n ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')} data-testid={'notif-score-' + n}>{n}</button>;
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenderSettings;
